/**
 * IndividualAwardsPanel.js — editable Individual Awards tables (OL SOT).
 * Projects to the WP /individual-awards/ page (Basketball). Sections can be
 * group headers (big dividers) or award tables of {year, recipients}.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IndividualAwardsPanel({ orgId }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/individual-awards/${orgId}`, { withCredentials: true })
      .then(({ data }) => setSections(data.sections || []))
      .catch((e) => setMsg({ ok: false, text: e.message }))
      .finally(() => setLoading(false));
  }, [orgId]);

  const update = (fn) => setSections((prev) => { const copy = JSON.parse(JSON.stringify(prev)); fn(copy); return copy; });
  const move = (i, dir) => update((s) => { const j = i + dir; if (j < 0 || j >= s.length) return; [s[i], s[j]] = [s[j], s[i]]; });

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await axios.put(`${API}/individual-awards/${orgId}`, { sections }, { withCredentials: true });
      setMsg({ ok: true, text: 'Saved. The Individual Awards page will reflect this shortly.' });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-gray-500" data-testid="ia-loading">Loading Individual Awards…</div>;

  return (
    <div className="space-y-6" data-testid="individual-awards-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Individual Awards</h2>
          <p className="text-sm text-gray-500">Editable awards tables shown on the WordPress Basketball → Individual Awards page. OmniLeagues is the source of truth.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="ia-add-section" onClick={() => update((s) => s.push({ title: 'New Section', group: false, entries: [] }))}
            className="px-3 py-2 text-sm border rounded hover:bg-gray-50">+ Section</button>
          <button data-testid="ia-save" onClick={save} disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save Awards'}</button>
        </div>
      </div>

      {msg && <div data-testid="ia-result" className={`p-2 rounded text-sm ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>{msg.text}</div>}

      {sections.map((sec, i) => (
        <div key={i} className={`border rounded p-4 ${sec.group ? 'bg-slate-50' : ''}`} data-testid={`ia-section-${i}`}>
          <div className="flex items-center gap-2 mb-3">
            <input value={sec.title} onChange={(e) => update((s) => { s[i].title = e.target.value; })}
              data-testid={`ia-section-title-${i}`}
              className={`flex-1 border rounded px-3 py-2 bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600 ${sec.group ? 'font-bold text-lg' : 'font-medium'}`} />
            <label className="text-xs flex items-center gap-1 whitespace-nowrap">
              <input type="checkbox" checked={!!sec.group} onChange={(e) => update((s) => { s[i].group = e.target.checked; })} data-testid={`ia-section-group-${i}`} />
              Header
            </label>
            <button onClick={() => move(i, -1)} className="px-2 py-1 text-xs border rounded">↑</button>
            <button onClick={() => move(i, 1)} className="px-2 py-1 text-xs border rounded">↓</button>
            <button onClick={() => update((s) => s.splice(i, 1))} data-testid={`ia-section-del-${i}`} className="px-2 py-1 text-xs border rounded text-red-600">Delete</button>
          </div>

          {!sec.group && (
            <div className="space-y-2">
              {sec.entries.map((en, j) => (
                <div key={j} className="flex gap-2 items-start">
                  <input value={en.year} onChange={(e) => update((s) => { s[i].entries[j].year = e.target.value; })}
                    placeholder="Year" className="w-24 border rounded px-2 py-1 text-sm bg-white text-gray-900 placeholder:text-gray-400 dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600 dark:placeholder:text-gray-500" data-testid={`ia-entry-year-${i}-${j}`} />
                  <textarea value={en.text} onChange={(e) => update((s) => { s[i].entries[j].text = e.target.value; })}
                    placeholder="Recipients" rows={1} className="flex-1 border rounded px-2 py-1 text-sm bg-white text-gray-900 placeholder:text-gray-400 dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600 dark:placeholder:text-gray-500" data-testid={`ia-entry-text-${i}-${j}`} />
                  <button onClick={() => update((s) => s[i].entries.splice(j, 1))} className="px-2 py-1 text-xs border rounded text-red-600">✕</button>
                </div>
              ))}
              <button onClick={() => update((s) => s[i].entries.push({ year: '', text: '' }))}
                data-testid={`ia-add-entry-${i}`} className="px-3 py-1 text-xs border rounded hover:bg-gray-50">+ Row</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
